import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  GraduationCap,
  Users,
  Trophy,
  Mail,
  Phone,
  MapPin,
  Send,
  Heart,
  Activity,
} from "lucide-react";
import {
  siteConfig,
  heroImage,
  aboutFeatures,
  stats,
} from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import { openExternal } from "../lib/external";

const iconMap = { GraduationCap, Users, Trophy };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [athleteCount, setAthleteCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/home-stats`, { timeout: 15000 })
      .then((res) => {
        if (!cancelled) setAthleteCount(res?.data?.athlete_count ?? null);
      })
      .catch(() => {
        if (!cancelled) setAthleteCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const payload = {
      name: (formData.get("name") || "").toString().trim(),
      email: (formData.get("email") || "").toString().trim(),
      subject: (formData.get("subject") || "").toString().trim(),
      message: (formData.get("message") || "").toString().trim(),
    };

    if (!payload.name || !payload.email || !payload.message) {
      toast({
        title: "Campos em falta",
        description: "Preenche nome, email e mensagem.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, payload, { timeout: 30000 });
      toast({
        title: "Mensagem enviada!",
        description:
          "Obrigado pelo contacto. Responderemos para o teu email em breve.",
      });
      form.reset();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Nao foi possivel enviar a mensagem. Tenta novamente.";
      toast({
        title: "Erro ao enviar",
        description: detail,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-brand-700 text-white">
        <div
          className="absolute inset-0 -z-0 opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(circle at 15% 20%, rgba(245,130,32,0.18), transparent 45%), radial-gradient(circle at 85% 80%, rgba(229,50,109,0.18), transparent 45%)",
          }}
        />
        <div className="absolute inset-0 -z-0" style={{
          backgroundImage: "linear-gradient(180deg, rgba(22,18,92,0) 0%, rgba(22,18,92,0.4) 100%)"
        }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur text-white text-xs font-medium tracking-wide mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-ember-400 animate-pulse" />
              Época 2026/2027 · Inscrições abertas
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05]">
              Clube de Atletismo
              <span className="block bg-gradient-to-r from-ember-400 via-ember-500 to-ember-600 bg-clip-text text-transparent">
                de Torres Vedras
              </span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/80 leading-relaxed max-w-xl">
              {siteConfig.tagline}
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a
                href={siteConfig.registrationUrl}
                onClick={openExternal(siteConfig.registrationUrl)}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-ember-400 to-ember-600 hover:from-ember-500 hover:to-ember-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 cursor-pointer"
              >
                Inscreve-te
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/equipa"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-white/30 hover:border-white text-white font-semibold transition-colors"
              >
                Conhece a Equipa
              </Link>
            </div>

            <a
              href={siteConfig.trialUrl}
              onClick={openExternal(siteConfig.trialUrl)}
              className="mt-4 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-brand-700 hover:bg-brand-50 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 cursor-pointer"
            >
              Vem Experimentar
              <ArrowRight className="w-4 h-4" />
            </a>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              {stats.map((s) => (
                <div key={s.id}>
                  <p className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-ember-400 to-ember-600 bg-clip-text text-transparent">
                    {s.label === "Atletas" && athleteCount != null
                      ? athleteCount
                      : s.value}
                  </p>
                  <p className="text-xs uppercase tracking-wider text-white/60 mt-1">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-tr from-ember-400/30 via-ember-600/20 to-transparent blur-2xl" />
            <div className="relative rounded-[2rem] overflow-hidden border-2 border-white/10 shadow-2xl bg-brand-800 aspect-[4/5] lg:aspect-square">
              <img
                src={heroImage}
                alt="Atleta CATV em salto em altura"
                className="absolute inset-0 w-full h-full object-cover scale-[1.08]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-900/30 via-transparent to-transparent pointer-events-none" />
            </div>
            <div className="hidden md:flex absolute -bottom-6 -left-6 items-center gap-3 bg-white rounded-2xl shadow-xl px-5 py-4 border border-stone-100">
              <div className="w-11 h-11 rounded-full bg-ember-50 flex items-center justify-center text-ember-600">
                <Heart className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-stone-500">Fundado em</p>
                <p className="font-bold text-stone-900">{siteConfig.founded}</p>
              </div>
            </div>
            <div className="hidden md:flex absolute -top-6 -right-6 items-center gap-3 bg-white rounded-2xl shadow-xl px-5 py-4 border border-stone-100">
              <div className="w-11 h-11 rounded-full bg-brand-50 flex items-center justify-center text-brand-700">
                <Activity className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-stone-500">Atletas</p>
                <p className="font-bold text-stone-900">
                  {athleteCount != null ? athleteCount : 69} activos
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOBRE NOS */}
      <section id="sobre" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="font-semibold text-sm uppercase tracking-widest mb-3 bg-gradient-to-r from-ember-500 to-ember-600 bg-clip-text text-transparent">
              Sobre Nós
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 tracking-tight">
              O CATV é mais do que um clube—
              <span className="text-brand-700">somos uma família.</span>
            </h2>
            <p className="mt-5 text-stone-600 text-lg leading-relaxed">
              Formamos atletas e promovemos o desporto em Torres Vedras.
              Acreditamos que o atletismo transforma vidas—uma passada de cada
              vez.
            </p>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {aboutFeatures.map((f) => {
              const Icon = iconMap[f.icon];
              return (
                <div
                  key={f.id}
                  className="group relative p-8 rounded-2xl bg-stone-50 hover:bg-white border border-stone-100 hover:border-brand-200 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-xl bg-brand-50 group-hover:bg-gradient-to-br group-hover:from-ember-400 group-hover:to-ember-600 flex items-center justify-center text-brand-700 group-hover:text-white transition-colors mb-5">
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">
                    {f.title}
                  </h3>
                  <p className="text-stone-600 leading-relaxed">
                    {f.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="relative overflow-hidden rounded-3xl bg-brand-700 px-8 md:px-16 py-16 md:py-20">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 80% 20%, rgba(245,130,32,0.35), transparent 45%), radial-gradient(circle at 10% 80%, rgba(229,50,109,0.25), transparent 45%)",
              }}
            />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                  Época 2026/2027 arranca já em Setembro!
                </h3>
                <p className="mt-4 text-white/80 text-lg">
                  Não fiques de fora. Vem treinar connosco e descobre o teu
                  potencial.
                </p>
              </div>
              <div className="flex md:justify-end">
                <a
                  href={siteConfig.registrationUrl}
                  onClick={openExternal(siteConfig.registrationUrl)}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-ember-400 to-ember-600 hover:from-ember-500 hover:to-ember-700 text-white font-bold shadow-xl transition-colors cursor-pointer"
                >
                  Junta-te a nós
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACTOS */}
      <section id="contactos" className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14">
          <div>
            <p className="font-semibold text-sm uppercase tracking-widest mb-3 bg-gradient-to-r from-ember-500 to-ember-600 bg-clip-text text-transparent">
              Contactos
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 tracking-tight">
              Estamos aqui para ti.
            </h2>
            <p className="mt-5 text-stone-600 text-lg">
              Tens dúvidas, queres saber mais sobre os treinos ou simplesmente
              dizer olá? Envia-nos uma mensagem.
            </p>

            <ul className="mt-10 space-y-5">
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center text-brand-700 shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-stone-500">
                    Email
                  </p>
                  <a
                    href={`mailto:${siteConfig.email}`}
                    className="text-stone-900 font-semibold hover:text-brand-700 transition-colors"
                  >
                    {siteConfig.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center text-brand-700 shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-stone-500">
                    Telefone
                  </p>
                  <p className="text-stone-900 font-semibold">
                    {siteConfig.phone}
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center text-brand-700 shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-stone-500">
                    Localização
                  </p>
                  <p className="text-stone-900 font-semibold">
                    {siteConfig.location}
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl shadow-xl border border-stone-100 p-8 md:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">
                  Nome
                </label>
                <Input required name="name" placeholder="O teu nome" />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">
                  Email
                </label>
                <Input required type="email" name="email" placeholder="email@exemplo.pt" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">
                Assunto
              </label>
              <Input name="subject" placeholder="Sobre o que queres falar?" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">
                Mensagem
              </label>
              <Textarea required name="message" rows={5} placeholder="A tua mensagem..." />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-brand-700 hover:bg-brand-800 disabled:opacity-60 text-white rounded-full py-6 text-base font-semibold"
            >
              <Send className="w-4 h-4 mr-2" />
              {submitting ? "A enviar..." : "Enviar"}
            </Button>
          </form>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
